if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');

        this.selectors = {
          variantIdSelect: '[name="id"]',
        };
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }
      get quantity() {
        return this.querySelector('quantity-input');
      }
      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
      get productId() {
        return this.getAttribute('data-product-id');
      }
      get select() {
        return this.querySelector('select');
      }
      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
        this.mainProductInfo = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
        this.container = this.closest('.sticky-atc-bar');
        this.submitButton = this.querySelector('[type="submit"]');
        this.selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;

        this.variantData = this.getVariantData();

        this.init();
        this.select.addEventListener('change', (e) => {
          if (this.isUpdating) return;
          this.isUpdating = true;

          this.updateQuantityInput();

          this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
          const selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));

          if (this.mainVariantSelects) {
            Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
              const variantOptionVal = this.currentVariant.options[index];
              switch (element.tagName) {
                case 'SELECT':
                  element.value = variantOptionVal;
                  const options = element.querySelectorAll('option');
                  options.forEach((option) => option.removeAttribute('selected'));

                  element.value = variantOptionVal;
                  const selectedOption = element.querySelector(`option[value="${variantOptionVal}"]`);
                  if (selectedOption) {
                    selectedOption.setAttribute('selected', 'selected');
                  }
                  break;
                case 'FIELDSET':
                  Array.from(element.querySelectorAll('input')).forEach((radio) => {
                    if (radio.value === variantOptionVal) {
                      radio.checked = true;
                    }
                  });
                  break;
              }
            });
            setTimeout(() => {
              this.mainVariantSelects.dispatchEvent(new Event('change', { detail: { formStickty: true } }));
              this.isUpdating = false;
            }, 0);
          } else {
            this.isUpdating = false;
          }

          this.updatePrice();
          this.updateButton(true, '', false);
          if (!this.currentVariant) {
            this.updateButton(true, '', true);
          } else {
            const atcDisabled =
              !this.currentVariant.available &&
              !FoxTheme.variantSellsRegardlessOfStock?.(this.currentVariant);
            this.updateButton(atcDisabled, FoxTheme.variantStrings.soldOut);
          }
        });

        // Ring PDP: `properties[Ring size]` / engraving inputs use `form="{{ main product_form_id }}"`
        // (see `bm-ring-line-item-properties`). The sticky bar has its own `<form is="product-form">`,
        // so submit must run the **main** form or line-item properties are omitted. Intercept in
        // **capture** on this host so we run before the inner `ProductForm` bubble handler.
        this._boundStickyProductFormSubmit = (event) => this.handleStickyProductFormSubmitCapture(event);
        this.addEventListener('submit', this._boundStickyProductFormSubmit, true);
      }

      disconnectedCallback() {
        if (this._boundStickyProductFormSubmit) {
          this.removeEventListener('submit', this._boundStickyProductFormSubmit, true);
        }
        this._disconnectMainSubmitObserver();
      }

      /**
       * Ring-only line-item properties are associated with the main PDP form id, not the sticky
       * bar form. Legacy logic only looked for `[data-custom-property]` (ring size only), so
       * engraving-only or submit-via-keyboard still hit the sticky `ProductForm` and skipped props.
       */
      ringLineItemPropertiesOnMainPdp() {
        if (!this.mainProductInfo) return null;
        return this.mainProductInfo.querySelector('.bm-ring-line-item-properties');
      }

      _setStickySubmitLoading() {
        const btn = this.submitButton;
        if (!btn) return;
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('btn--loading');
      }

      _clearStickySubmitLoading() {
        const btn = this.submitButton;
        if (!btn) return;
        btn.classList.remove('btn--loading');
        btn.removeAttribute('aria-disabled');
      }

      _disconnectMainSubmitObserver() {
        if (typeof this._mainSubmitObserverDisconnect === 'function') {
          this._mainSubmitObserverDisconnect();
          this._mainSubmitObserverDisconnect = null;
        }
      }

      /**
       * When sticky submit is delegated to the main PDP form, mirror ProductForm loading on the
       * sticky button and clear it when the main submit finishes (or never starts).
       */
      _observeMainSubmitButtonForStickyLoading(mainSubmitButton) {
        this._disconnectMainSubmitObserver();
        if (!(mainSubmitButton instanceof HTMLElement)) return;

        const observer = new MutationObserver(() => {
          if (!mainSubmitButton.hasAttribute('aria-disabled')) {
            this._clearStickySubmitLoading();
            observer.disconnect();
            this._mainSubmitObserverDisconnect = null;
          }
        });
        observer.observe(mainSubmitButton, { attributes: true, attributeFilter: ['aria-disabled'] });
        this._mainSubmitObserverDisconnect = () => observer.disconnect();
      }

      handleStickyProductFormSubmitCapture(event) {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.getAttribute('is') !== 'product-form') return;
        if (form !== this.productForm) return;
        if (!this.productFormActions) return;
        if (!this.ringLineItemPropertiesOnMainPdp()) return;

        if (this.submitButton?.hasAttribute('aria-disabled')) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const stickyVariant = this.querySelector(this.selectors.variantIdSelect);
        const mainVariant = this.productFormActions.querySelector(this.selectors.variantIdSelect);
        if (stickyVariant && mainVariant && stickyVariant instanceof HTMLInputElement) {
          mainVariant.value = stickyVariant.value;
          if (stickyVariant.disabled) {
            mainVariant.setAttribute('disabled', 'disabled');
          } else {
            mainVariant.removeAttribute('disabled');
          }
        }

        const stickyQtyInput = this.querySelector('quantity-input input[name="quantity"], input[name="quantity"]');
        const mainSectionId = this.mainProductInfo?.dataset?.section;
        const mainQty =
          (mainSectionId && document.getElementById(`Quantity-${mainSectionId}`)) ||
          this.productFormActions.querySelector('input[name="quantity"]');
        if (
          stickyQtyInput &&
          mainQty &&
          stickyQtyInput instanceof HTMLInputElement &&
          mainQty instanceof HTMLInputElement
        ) {
          mainQty.value = stickyQtyInput.value;
        }

        const mainFormId = this.productFormActions.id || (this.sectionId && `product-form-${this.sectionId}`);
        const mainProductForm =
          (mainFormId && document.getElementById(mainFormId)) ||
          (this.productFormActions.matches?.('form[is="product-form"]')
            ? this.productFormActions
            : this.productFormActions.querySelector?.('form[is="product-form"]'));

        if (
          mainProductForm &&
          typeof mainProductForm.validateRequiredFields === 'function' &&
          !mainProductForm.validateRequiredFields()
        ) {
          this._clearStickySubmitLoading();
          return;
        }

        this._setStickySubmitLoading();
        this.productFormActions.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          try {
            this.productFormActions.requestSubmit();
          } catch (err) {
            console.error(err);
            this._clearStickySubmitLoading();
            this._disconnectMainSubmitObserver();
            return;
          }

          const mainSubmit = this.productFormActions.querySelector('[type="submit"]');
          if (mainSubmit?.hasAttribute('aria-disabled')) {
            this._observeMainSubmitButtonForStickyLoading(mainSubmit);
          } else {
            this._clearStickySubmitLoading();
          }
        }, 0);
      }

      updateQuantityInput() {
        this.currentVariant = this.variantData.find((variant) => variant.id === Number(this.selectedVariantId));
        fetch(`${this.getAttribute('data-url')}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const quantity = document.querySelector(`#QuantitySticky-${this.sectionId}`);
            const newQuantity = parsedHTML.querySelector(`#QuantitySticky-${this.sectionId}`);
            if (newQuantity) {
              quantity.innerHTML = newQuantity.innerHTML;
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {});
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = `-80px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const method = entry.intersectionRatio !== 1 ? 'add' : 'remove';
              this.container.classList[method]('sticky-atc-bar--show');
            });
          },
          { threshold: 1, rootMargin }
        );
        this.setObserveTarget();
        this.syncWithMainProductForm();
      }

      setObserveTarget() {
        this.observer.observe(this.productFormActions);
        this.observeTarget = this.productFormActions;
      }

      checkDevice(e) {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('#product-form-sticky-atc-bar');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updatePrice() {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out (mirrors main PDP: not tracked or continue policy still sells).
        if (
          !this.currentVariant.available &&
          !FoxTheme.variantSellsRegardlessOfStock?.(this.currentVariant)
        ) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat))
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${FoxTheme.Currency.getBaseUnit(this.currentVariant)}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          this.currentVariant = variant;
          variantInput.value = variant.id;
          this.updatePrice();
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            const atcDisabled =
              !variant.available && !FoxTheme.variantSellsRegardlessOfStock?.(variant);
            this.updateButton(atcDisabled, FoxTheme.variantStrings.soldOut);
          }

          this.updateQuantityInput();
        });
      }
    }
  );
}
